import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Button, Input } from '../../common';
import tenantsService from '../../../services/api/tenants.service';

const TenantModal = ({ tenant, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    email: '',
    password: '',
    provider: 'official',
    // WhatsApp Official
    waPhoneNumberId: '',
    waAccessToken: '',
    waAppSecret: '',
    graphApiVersion: 'v20.0',
    waTemplateName: 'follow_up_lembrete',
    waTemplateLang: 'pt_BR',
    // Evolution API
    evolutionApiUrl: '',
    evolutionApiKey: '',
    evolutionInstance: '',
    // Comum
    defaultFlowId: 'real_estate_lead',
    coexEnable: true,
    coexAutoResumeM: 0,
    coexIgnoreBotMs: 5000,
    isActive: true,
    botPaused: false,
    sendDisabled: false,
    sendProtocol: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tenant) {
      setFormData({
        id: tenant.id || '',
        name: tenant.name || '',
        email: tenant.email || '',
        password: '', // Não preenche senha ao editar
        provider: tenant.provider || tenant.waProvider || 'official',
        // WhatsApp Official
        waPhoneNumberId: tenant.waPhoneNumberId || '',
        waAccessToken: tenant.waAccessToken || '',
        waAppSecret: tenant.waAppSecret || '',
        graphApiVersion: tenant.graphApiVersion || 'v20.0',
        waTemplateName: tenant.waTemplateName || 'follow_up_lembrete',
        waTemplateLang: tenant.waTemplateLang || 'pt_BR',
        // Evolution API
        evolutionApiUrl: tenant.evolutionApiUrl || '',
        evolutionApiKey: tenant.evolutionApiKey || '',
        evolutionInstance: tenant.evolutionInstance || '',
        // Comum
        defaultFlowId: tenant.defaultFlowId || 'real_estate_lead',
        coexEnable: tenant.coexEnable ?? true,
        coexAutoResumeM: tenant.coexAutoResumeM || 0,
        coexIgnoreBotMs: tenant.coexIgnoreBotMs || 5000,
        isActive: tenant.isActive ?? true,
        botPaused: tenant.botPaused ?? false,
        sendDisabled: tenant.sendDisabled ?? false,
        sendProtocol: tenant.sendProtocol ?? false,
      });
    }
  }, [tenant]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Nome sempre obrigatório
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    // Email e senha obrigatórios apenas ao CRIAR
    if (!tenant) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email é obrigatório';
      }
      if (!formData.password.trim()) {
        newErrors.password = 'Senha é obrigatória';
      }

      // Validações de provider apenas ao CRIAR
      if (formData.provider === 'official') {
        if (!formData.waPhoneNumberId.trim()) {
          newErrors.waPhoneNumberId = 'Phone Number ID é obrigatório';
        }
        if (!formData.waAccessToken.trim()) {
          newErrors.waAccessToken = 'Access Token é obrigatório';
        }
        if (!formData.waAppSecret.trim()) {
          newErrors.waAppSecret = 'App Secret é obrigatório';
        }
      } else if (formData.provider === 'evolution') {
        if (!formData.evolutionApiUrl.trim()) {
          newErrors.evolutionApiUrl = 'API URL é obrigatório';
        }
        if (!formData.evolutionApiKey.trim()) {
          newErrors.evolutionApiKey = 'API Key é obrigatório';
        }
        if (!formData.evolutionInstance.trim()) {
          newErrors.evolutionInstance = 'Instance Name é obrigatório';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const dataToSend = { ...formData };
      
      // Remove ID vazio ao criar
      if (!tenant && !dataToSend.id.trim()) {
        delete dataToSend.id;
      }

      // Remove senha vazia ao editar
      if (tenant && !dataToSend.password) {
        delete dataToSend.password;
      }

      // Remove email ao editar (não pode mudar)
      if (tenant) {
        delete dataToSend.email;
      }

      // Remove campos vazios ao editar (não obrigatórios)
      if (tenant) {
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === '' && 
              key !== 'name' && 
              key !== 'defaultFlowId' &&
              key !== 'graphApiVersion' &&
              key !== 'waTemplateName' &&
              key !== 'waTemplateLang' &&
              key !== 'coexAutoResumeM' &&
              key !== 'coexIgnoreBotMs') {
            delete dataToSend[key];
          }
        });
      }

      // Converte números
      if (dataToSend.coexAutoResumeM !== undefined) {
        dataToSend.coexAutoResumeM = parseInt(dataToSend.coexAutoResumeM) || 0;
      }
      if (dataToSend.coexIgnoreBotMs !== undefined) {
        dataToSend.coexIgnoreBotMs = parseInt(dataToSend.coexIgnoreBotMs) || 5000;
      }

      // Remove campos não utilizados baseado no provider
      if (dataToSend.provider === 'evolution') {
        delete dataToSend.waPhoneNumberId;
        delete dataToSend.waAccessToken;
        delete dataToSend.waAppSecret;
        delete dataToSend.graphApiVersion;
        delete dataToSend.waTemplateName;
        delete dataToSend.waTemplateLang;
      } else if (dataToSend.provider === 'official') {
        delete dataToSend.evolutionApiUrl;
        delete dataToSend.evolutionApiKey;
        delete dataToSend.evolutionInstance;
      }

      if (tenant) {
        await tenantsService.update(tenant.id, dataToSend);
      } else {
        await tenantsService.createOrUpdate(dataToSend);
      }

      onSuccess();
    } catch (error) {
      alert('Erro ao salvar tenant: ' + (error.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {tenant ? 'Editar Tenant' : 'Novo Tenant'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] custom-scrollbar">
          <div className="space-y-6">
            {/* Informações Básicas */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="ID do Tenant"
                  name="id"
                  value={formData.id}
                  onChange={handleChange}
                  placeholder="tenant_123"
                  disabled={!!tenant}
                  helperText={tenant ? "ID não pode ser alterado" : "Deixe em branco para usar 'default'"}
                />

                <Input
                  label="Nome"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Minha Empresa LTDA"
                  error={errors.name}
                  required
                />
              </div>

              {!tenant && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="admin@empresa.com"
                    error={errors.email}
                    required
                    helperText="Usado para login do tenant"
                  />

                  <Input
                    label="Senha"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="********"
                    error={errors.password}
                    required
                    helperText="Mínimo 8 caracteres"
                  />
                </div>
              )}

              {tenant && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    ℹ️ Email não pode ser alterado. Para atualizar campos, simplesmente edite os valores desejados. Deixe em branco para manter o valor atual.
                  </p>
                </div>
              )}
            </div>

            {/* Seleção de Provider */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">WhatsApp Provider</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.provider === 'official' ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-300'
                } ${tenant ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="provider"
                    value="official"
                    checked={formData.provider === 'official'}
                    onChange={handleChange}
                    disabled={!!tenant}
                    className="sr-only"
                  />
                  <div className={`flex-1 ${formData.provider === 'official' ? 'text-primary-700' : 'text-gray-600'}`}>
                    <p className="font-semibold">WhatsApp Official API</p>
                    <p className="text-sm mt-1">Meta Business API</p>
                  </div>
                  {formData.provider === 'official' && (
                    <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </label>

                <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.provider === 'evolution' ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-300'
                } ${tenant ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="provider"
                    value="evolution"
                    checked={formData.provider === 'evolution'}
                    onChange={handleChange}
                    disabled={!!tenant}
                    className="sr-only"
                  />
                  <div className={`flex-1 ${formData.provider === 'evolution' ? 'text-primary-700' : 'text-gray-600'}`}>
                    <p className="font-semibold">Evolution API</p>
                    <p className="text-sm mt-1">Baileys/WhatsApp Web</p>
                  </div>
                  {formData.provider === 'evolution' && (
                    <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </label>
              </div>
              {tenant && (
                <p className="text-xs text-gray-500 mt-2">
                  ⚠️ O provider não pode ser alterado após a criação
                </p>
              )}
            </div>

            {/* Configurações WhatsApp Official */}
            {formData.provider === 'official' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações WhatsApp Official</h3>
                <div className="space-y-4">
                  <Input
                    label="Phone Number ID"
                    name="waPhoneNumberId"
                    value={formData.waPhoneNumberId}
                    onChange={handleChange}
                    placeholder="123456789012345"
                    error={errors.waPhoneNumberId}
                    required={!tenant}
                    helperText={tenant ? "Deixe em branco para manter o atual" : undefined}
                  />

                  <Input
                    label="Access Token"
                    name="waAccessToken"
                    value={formData.waAccessToken}
                    onChange={handleChange}
                    placeholder="EAAxxxxxxxxxxxxxxx"
                    error={errors.waAccessToken}
                    required={!tenant}
                    type="password"
                    helperText={tenant ? "Deixe em branco para manter o atual" : undefined}
                  />

                  <Input
                    label="App Secret"
                    name="waAppSecret"
                    value={formData.waAppSecret}
                    onChange={handleChange}
                    placeholder="1234567890abcdef1234567890abcdef"
                    error={errors.waAppSecret}
                    required={!tenant}
                    type="password"
                    helperText={tenant ? "Deixe em branco para manter o atual" : undefined}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Graph API Version"
                      name="graphApiVersion"
                      value={formData.graphApiVersion}
                      onChange={handleChange}
                      placeholder="v20.0"
                    />

                    <Input
                      label="Template Name"
                      name="waTemplateName"
                      value={formData.waTemplateName}
                      onChange={handleChange}
                      placeholder="follow_up_lembrete"
                    />

                    <Input
                      label="Template Language"
                      name="waTemplateLang"
                      value={formData.waTemplateLang}
                      onChange={handleChange}
                      placeholder="pt_BR"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Configurações Evolution API */}
            {formData.provider === 'evolution' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Evolution API</h3>
                <div className="space-y-4">
                  <Input
                    label="Evolution API URL"
                    name="evolutionApiUrl"
                    value={formData.evolutionApiUrl}
                    onChange={handleChange}
                    placeholder="http://evolution:8080"
                    error={errors.evolutionApiUrl}
                    required={!tenant}
                    helperText={tenant ? "Deixe em branco para manter o atual" : "URL da sua instância Evolution API"}
                  />

                  <Input
                    label="Evolution API Key"
                    name="evolutionApiKey"
                    value={formData.evolutionApiKey}
                    onChange={handleChange}
                    placeholder="B6D711FCDE4D4FD5936544120E713976"
                    error={errors.evolutionApiKey}
                    required={!tenant}
                    type="password"
                    helperText={tenant ? "Deixe em branco para manter o atual" : undefined}
                  />

                  <Input
                    label="Instance Name"
                    name="evolutionInstance"
                    value={formData.evolutionInstance}
                    onChange={handleChange}
                    placeholder="marabot-qr9726"
                    error={errors.evolutionInstance}
                    required={!tenant}
                    helperText={tenant ? "Deixe em branco para manter o atual" : "Nome único da instância no Evolution"}
                  />
                </div>
              </div>
            )}

            {/* Configurações de Fluxo */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações de Fluxo</h3>
              <Input
                label="Default Flow ID"
                name="defaultFlowId"
                value={formData.defaultFlowId}
                onChange={handleChange}
                placeholder="real_estate_lead"
              />
            </div>

            {/* Coexistência */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Coexistência com Agentes</h3>
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="coexEnable"
                    checked={formData.coexEnable}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Habilitar Coexistência</span>
                    <p className="text-sm text-gray-600">Permite transferência para agentes humanos</p>
                  </div>
                </label>

                {formData.coexEnable && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8">
                    <Input
                      label="Auto Resume (minutos)"
                      name="coexAutoResumeM"
                      type="number"
                      value={formData.coexAutoResumeM}
                      onChange={handleChange}
                      helperText="0 = desabilitado"
                    />

                    <Input
                      label="Ignore Bot Messages (ms)"
                      name="coexIgnoreBotMs"
                      type="number"
                      value={formData.coexIgnoreBotMs}
                      onChange={handleChange}
                      helperText="Janela de tempo para ignorar bot"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Status e Controles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status e Controles</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Tenant Ativo</span>
                    <p className="text-xs text-gray-500">Habilita o tenant na plataforma</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="botPaused"
                    checked={formData.botPaused}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Bot Pausado</span>
                    <p className="text-xs text-gray-500">Pausa automação do bot</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="sendDisabled"
                    checked={formData.sendDisabled}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-red-500 focus:ring-red-400"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Desabilitar Envio de Mensagens</span>
                    <p className="text-xs text-gray-500">Impede envio de mensagens pelo bot</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="sendProtocol"
                    checked={formData.sendProtocol}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Enviar Protocolo</span>
                    <p className="text-xs text-gray-500">Envia número de protocolo ao cliente</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            {tenant ? 'Atualizar' : 'Criar'} Tenant
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TenantModal;